import React, { useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import {
  Button,
  Checkbox,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from '@roger/ui-components';
import {
  client,
  getActiveBillingCycles,
  sendBillingCycleToClearinghouse,
} from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useToast } from '../../../hooks/useToastContext';

interface EOEReadyToSendEntryProps {
  entry: ActiveBillingCycleItem;
  index?: number;
  isSelected: boolean;
  onToggle: (id: string) => void;
  onEdit: (entry: ActiveBillingCycleItem) => void;
}

interface EOEReadyToSendTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEReadyToSendEntry: React.FC<EOEReadyToSendEntryProps> = ({
  entry,
  index,
  isSelected,
  onToggle,
  onEdit,
}) => {
  const startDate = formatDateToMMDDYYYY(entry.billing_period_start);
  const endDate = formatDateToMMDDYYYY(entry.billing_period_end);
  const isAlternate = index !== undefined && index % 2 === 1;

  return (
    <TableRow
      className={
        isAlternate
          ? 'bg-muted hover:bg-muted'
          : 'bg-background hover:bg-background'
      }
    >
      <TableCell>
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(entry.billing_cycle_id)}
        />
      </TableCell>
      <TableCell>{entry.patient_name}</TableCell>
      <TableCell>{entry.patient_mrn}</TableCell>
      <TableCell>{entry.branch}</TableCell>
      <TableCell>{entry.insurance_name}</TableCell>
      <TableCell>
        <span>{startDate} -</span>
        <br />
        <span>{endDate}</span>
      </TableCell>
      <TableCell>{entry.claim_number}</TableCell>
      <TableCell>{entry.claim_status}</TableCell>
      <TableCell>{entry.tob}</TableCell>
      <TableCell>{entry.total_charges}</TableCell>
      <TableCell>
        <Button
          type='button'
          variant='ghost'
          size='icon'
          onClick={() => onEdit(entry)}
          className='text-muted-foreground hover:text-foreground'
          aria-label='Edit billing cycle'
        >
          <Pencil className='h-4 w-4' />
        </Button>
      </TableCell>
    </TableRow>
  );
};

const EOEReadyToSendTab: React.FC<EOEReadyToSendTabProps> = ({
  onCountsInvalidated,
}) => {
  const [editEntry, setEditEntry] = useState<ActiveBillingCycleItem | null>(
    null
  );
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const { error: showError } = useToast();

  const {
    data,
    isLoading,
    isSubmitting,
    selectedIds,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
  } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['READY_TO_SEND'] },
    errorMessage: 'Could not fetch data for the "Ready to Send" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
  });

  const getErrorStatus = (error: unknown): number | undefined => {
    const maybeAxiosError = error as {
      response?: { status?: number };
      status?: number;
    };
    return maybeAxiosError.response?.status ?? maybeAxiosError.status;
  };

  const getErrorDetail = (error: unknown): string | undefined => {
    const maybeAxiosError = error as { response?: { data?: unknown } };
    const data = maybeAxiosError.response?.data;
    if (typeof data === 'string') return data;
    if (
      typeof data === 'object' &&
      data !== null &&
      'detail' in data &&
      typeof (data as { detail?: unknown }).detail === 'string'
    ) {
      return (data as { detail: string }).detail;
    }
    return undefined;
  };

  const handleConfirmSend = () => {
    setSendConfirmOpen(false);
    void mutateSelected(
      id =>
        sendBillingCycleToClearinghouse({
          client,
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles failed to send',
      'The following billing cycles could not be updated',
      onCountsInvalidated,
      failures => {
        const first = failures[0];
        const status = getErrorStatus(first?.error);
        const detail = getErrorDetail(first?.error);

        showError(
          'Failed to generate claim',
          status === 500
            ? 'Unexpected error occurred. Please try again or contact support'
            : (detail ??
                'Unexpected error occurred. Please try again or contact support')
        );
      }
    );
  };

  return (
    <div className='flex flex-col gap-4'>
      {isLoading ? (
        <p className='text-muted-foreground text-center py-10'>Loading...</p>
      ) : data.length === 0 ? (
        <p className='text-muted-foreground text-center py-10'>
          There are currently no records to display
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Checkbox
                  checked={data.length > 0 && selectedIds.size === data.length}
                  onCheckedChange={handleToggleAll}
                />
              </TableHead>
              <TableHead>Patient Name</TableHead>
              <TableHead>MRN</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Insurance</TableHead>
              <TableHead>Billing Period</TableHead>
              <TableHead>Claim #</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>TOB</TableHead>
              <TableHead>Total Charges</TableHead>
              <TableHead>Edit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((entry, index) => (
              <EOEReadyToSendEntry
                key={entry.billing_cycle_id}
                entry={entry}
                index={index}
                isSelected={selectedIds.has(entry.billing_cycle_id)}
                onToggle={handleToggleId}
                onEdit={setEditEntry}
              />
            ))}
          </TableBody>
        </Table>
      )}
      <ConfirmationDialog
        open={sendConfirmOpen}
        title='Send claims?'
        message='This will send the claim to the clearinghouse and cannot be undone.'
        confirmLabel='Send'
        onAccept={handleConfirmSend}
        onCancel={() => setSendConfirmOpen(false)}
      />
      <Dialog
        open={editEntry !== null}
        onOpenChange={open => !open && setEditEntry(null)}
      >
        <DialogContent className='max-w-2xl max-h-[80vh] overflow-hidden flex flex-col'>
          <DialogHeader>
            <DialogTitle>Edit Billing Cycle</DialogTitle>
          </DialogHeader>
          <pre className='overflow-auto flex-1 text-xs bg-muted rounded p-4 whitespace-pre-wrap break-all'>
            {JSON.stringify(editEntry, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
      <div className='flex justify-end'>
        <Button
          variant='purple'
          disabled={selectedIds.size === 0 || isSubmitting || isLoading}
          onClick={() => setSendConfirmOpen(true)}
        >
          {isSubmitting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Send
        </Button>
      </div>
    </div>
  );
};

export default EOEReadyToSendTab;
